// React
import * as React from "react";

// Date Packages
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

// Components
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { StyledErrorMessage } from "src/components/application/styled-components/DropZone";
import { InputWrapper } from "../styles/DcaFields";

// Store
import { useAppDispatch } from "src/store/store";

// Slices
import { setEndDate, setStartDate, useDcaSlice } from "src/store/features/dca.slice";
import { trackEvent } from "src/Amplitude";

dayjs.extend(utc);
dayjs.extend(timezone);

type BasicDatePickerProps = {
  source: "start date" | "end date";
};

export default function BasicDatePicker({ source }: BasicDatePickerProps) {
  const { startDate, endDate } = useDcaSlice();
  const dispatch = useAppDispatch();
  const [error, setError] = React.useState("");

  const today = new Date();
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedDate = dayjs(today).tz(userTimeZone);
  const defaultSelection =
    source === "start date"
      ? formattedDate
      : endDate
      ? dayjs(endDate).tz(userTimeZone)
      : formattedDate.add(1, "day");

  const [value, setValue] = React.useState<Dayjs>(defaultSelection);

  React.useEffect(() => {
    const selectedDate = value.endOf("day").valueOf();
    if (source === "start date" && value) {
      dispatch(setStartDate(selectedDate));
      trackEvent("Changed start date", { selectedDate }, "DCA");
    }
    if (source === "end date" && value) {
      dispatch(setEndDate(selectedDate));
      trackEvent("Changed end date", { selectedDate }, "DCA");
    }
  }, [value]);

  // if start date picker is selected, set the min date to today
  // if end date picker is selected, set the min date to the day after the start date
  const minDate =
    source === "start date" ? formattedDate : dayjs(startDate).tz(userTimeZone).add(1, "day");

  return (
    <>
      <InputWrapper style={{ padding: "8.5px" }} error={error === "minDate"}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={value}
            showDaysOutsideCurrentMonth
            format="DD MMM YYYY"
            onError={(reason) => {
              if (reason) setError(reason);
            }}
            onChange={(newValue) => {
              if (newValue) setValue(newValue as Dayjs);
              setError("");
            }}
            slotProps={{
              popper: {
                sx: {
                  ".MuiPaper-root": {
                    borderRadius: "10px",
                    minWidth: "332px",
                    boxShadow: "0px 4px 20px 0px rgba(173, 192, 203, 0.30);",
                  },
                },
              },
              textField: {
                variant: "standard",
                placeholder: source === "start date" ? "Select start date" : "Select end date",
                InputProps: {
                  disableUnderline: true,
                  sx: {
                    ".MuiInputBase-input": {
                      padding: "0px",
                      width: "100%",
                      fontSize: "16px",
                      fontFamily: "Söhne",
                    },
                  },
                },
                sx: {
                  width: "100%",
                },
              },
            }}
            minDate={minDate}
          />
        </LocalizationProvider>
      </InputWrapper>
      {error === "minDate" && (
        <StyledErrorMessage>{`End date needs to be after start date`}</StyledErrorMessage>
      )}
    </>
  );
}
